import { uniqueId } from 'lodash';

export const getIds = <T extends Nullable<{ _id?: string; id?: string }>>(array: T[]): string[] => {
  return array.map(item => item?._id ?? item?.id ?? '');
};

export const concatIdsWithIndex = (ids: string[]): string[] => {
  return ids.map((id, idx) => `${id}[${idx}`);
};

export const splitIdsWithIndex = (ids: string[]): string[] => {
  return ids.map(id => {
    const idx = id.indexOf('[');

    return id.slice(0, idx);
  });
};

export const chunkIntoN = <T extends {}>(array: T[], n: number): T[][] => {
  const size = Math.ceil(array.length / n);

  return Array.from({ length: n }, (v, i) => array.slice(i * size, i * size + size));
};

export const combineChunks = <T extends {}>(chunks1: T[][], chunks2: T[][]): T[][] => {
  const maxLength = Math.max(chunks1.length, chunks2.length);

  return Array.from({ length: maxLength }, (_, i) => [
    ...(chunks1[i] || []),
    ...(chunks2[i] || []),
  ]);
};

export function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex < 0 ? 0 : startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

export const reorderBatchByIds = <T extends {}>({
  data,
  addedIndex,
  selectedIds,
  removedIndex,
}: {
  data: T[];
  addedIndex: number;
  selectedIds: string[];
  removedIndex: number;
}): T[] => {
  // @ts-ignore
  if (!data[0]._id) {
    return reorder(data, addedIndex, removedIndex);
  }
  // @ts-ignore
  const targetId = data[addedIndex]?._id;
  const sourceSet = new Set(selectedIds.filter(id => id !== targetId).map(id => id.toString()));
  // @ts-ignore
  const itemsToMove = data.filter(item => sourceSet.has(item._id.toString()));
  // @ts-ignore
  const remainingItems = data.filter(item => !sourceSet.has(item._id.toString()));

  if (targetId) {
    const targetIndex = remainingItems.findIndex(
      // @ts-ignore
      item => item._id.toString() === targetId.toString()
    );

    const shift = (addedIndex ?? 0) < removedIndex ? 0 : 1;

    return [
      ...remainingItems.slice(0, targetIndex + shift),
      ...itemsToMove,
      ...remainingItems.slice(targetIndex + shift),
    ];
  }

  return [...itemsToMove, ...remainingItems];
};

export const generateIds = <T extends {}>(array: T[]) => {
  return array.map(option => ({
    _id: uniqueId(),
    ...option,
  }));
};
